import React, { useEffect, useMemo } from 'react';
import NotificationTemplateServ from '@api/NotificationTemplates/NotificationTemplateServ';
import { resolveTemplateTexts } from '@api/NotificationTemplates/NotificationTemplates';
import { Stack, Tooltip, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import useCounter from '@hooks/useCounter';
import Counter from '@components/Utilities/Counter';
import IconButton from '@mui/material/IconButton';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import useOpenModal from '@hooks/useOpenModal';
import { ModalCloseCashRegister } from '@components/Collaborator/Modals/ModalCloseCashRegister';
import CashRegisterService from '@api/CashRegister/CashRegisterCtrl';
import { setCashRegister } from '@store/krispisStore';
import { getCashRegisterDate, isActiveCashRegister } from '@utils/CashRegisters';
import NotificationServ from '@api/Notifications/NotificationServ';
import { NotificationType } from '@api/Notifications/Notification';
import { BRANCH_LEAD_PROFILES } from '@utils/Permissions/Capabilities';

/**
 * Contador de la caja abierta en la barra superior.
 *
 * ⚠️ La caja vive en Redux PERSISTIDO (`krispis.cashRegister`), así que el widget
 * pintaba una caja aunque ① ya estuviera cerrada o borrada en Firestore, o
 * ② fuera de OTRA sucursal (cambiar de sucursal no la limpiaba). Por eso un admin
 * veía "caja abierta" de hace días en cualquier sucursal. Ahora:
 * - la píldora de CAJA CERRADA es solo la etiqueta ámbar (el renglón "Ábrela
 *   para vender" se quitó el 2026-08-29: quien vende ya lo sabe),
 * - solo se muestra si la caja es de la sucursal en operación y **del día de hoy**
 *   (2026-08-29: una caja de días anteriores seguía habilitando el POS), y
 * - al montar se revalida contra Firestore: si ya no existe o está cerrada, se
 *   saca de Redux (el POS pedirá abrir una nueva, que es lo correcto).
 */
const CashRegisterTime: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const { cashRegister } = useSelector((state: RootReducer) => state.krispis);
    const { branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);
    const cashRegisterService = useMemo(() => new CashRegisterService(), []);
    const [days, hours, minutes, seconds] = useCounter(
        cashRegister ? getCashRegisterDate(cashRegister.date) : new Date()
    );
    const modalCashRegister = useOpenModal();

    const isOpen = isActiveCashRegister(cashRegister, branchOfficeSelected?.id);

    /**
     * Avisa de las cajas que quedaron abiertas de días anteriores.
     *
     * Se hace AQUÍ, al entrar, porque no hay cron: el server escala a cero. La
     * consulta lee solo las cajas con estatus abierto —que son pocas por
     * definición— y corre una vez por sesión y sucursal, no en cada render.
     *
     * ⚠️ El aviso es idempotente por el id de la caja, así que aunque entren
     * cinco personas se crea UNA sola vez. Es exactamente el problema que el
     * 2026-08-29 tenía 32 cajas sueltas y $466,027 en cortes de varios días.
     */
    useEffect(() => {
        const branchOfficeId = branchOfficeSelected?.id;
        if (!branchOfficeId) return;
        const avisar = async () => {
            try {
                const rezagadas = await cashRegisterService.getOpenCashRegistersFromPreviousDays(branchOfficeId);
                if (rezagadas.length === 0) return;
                // Una sola lectura de overrides para todas las cajas del aviso — y
                // solo si hay algo que avisar (cada lectura se paga).
                const overrides = await NotificationTemplateServ.getOverrides();
                rezagadas.forEach((register) => {
                    if (!register.id) return;
                    const abierta = getCashRegisterDate(register.date).toLocaleDateString('es-MX');
                    void NotificationServ.notify({
                        // Texto editable desde el admin (staff_cash_register).
                        ...resolveTemplateTexts(
                            'staff_cash_register',
                            {
                                desde: abierta,
                                usuario: register.username ? ` (${register.username})` : ''
                            },
                            overrides
                        ),
                        type: NotificationType.CashRegister,
                        audience: {
                            profiles: BRANCH_LEAD_PROFILES,
                            branchOfficeIds: [branchOfficeId]
                        },
                        link: '/dashboard?tab=cashRegisters',
                        referenceId: `caja-sin-cerrar-${register.id}`
                    });
                });
            } catch (error) {
                // Sin conexión: no pasa nada, se revisa en la próxima sesión.
                console.error('No se pudieron revisar las cajas rezagadas:', error);
            }
        };
        void avisar();
    }, [branchOfficeSelected?.id, cashRegisterService]);

    // Sincroniza la caja de la sesión con la realidad de Firestore. Corre en la
    // barra, que está montada en todas las pantallas, así que el POS siempre
    // encuentra el dato correcto en Redux.
    //
    // ⚠️ `isOpen` está en las dependencias por la tablet que nunca se apaga: al
    // cruzar la MEDIANOCHE la caja de ayer deja de servir y hay que volver a
    // preguntar, o el dispositivo se quedaría con ella en Redux hasta que alguien
    // cambiara de sucursal. El re-render que lo dispara ya existe (el cronómetro
    // corre cada segundo).
    useEffect(() => {
        const sync = async () => {
            const branchOfficeId = branchOfficeSelected?.id;
            try {
                // ① ¿La caja que traemos sigue sirviendo?
                if (cashRegister?.id) {
                    const fresh = await cashRegisterService.getCashRegister(cashRegister.id);
                    if (fresh && isActiveCashRegister(fresh, branchOfficeId)) {
                        return;
                    }
                }
                // ② Si no, adoptar la que ya esté abierta HOY en esta sucursal —
                // la pudo abrir otra tablet, y antes cada sesión vivía en su propia
                // burbuja: el POS pedía abrir otra caja aunque ya hubiera una.
                const todays = branchOfficeId
                    ? await cashRegisterService.getTodayOpenCashRegister(branchOfficeId)
                    : undefined;
                dispatch(setCashRegister(todays));
            } catch (error) {
                // Sin conexión: se conserva lo persistido en vez de dejar al POS sin caja
            }
        };
        void sync();
    }, [cashRegister?.id, branchOfficeSelected?.id, isOpen]);

    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            {/* Píldora sobre el appbar oscuro: suelto se leía como texto perdido.
                Siempre visible — saber que la caja está CERRADA importa tanto como
                el contador, porque sin caja abierta no se pueden tomar pedidos. */}
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                px={1.5}
                py={0.75}
                borderRadius={2}
                sx={{
                    backgroundColor: isOpen ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 160, 0, 0.18)',
                    border: isOpen ? 'none' : '1px solid rgba(255, 160, 0, 0.5)'
                }}
            >
                <PointOfSaleIcon sx={{ fontSize: 20, color: isOpen ? 'rgba(255, 255, 255, 0.7)' : 'warning.light' }} />
                <Stack>
                    <Typography
                        variant="caption"
                        lineHeight={1.1}
                        sx={{
                            color: isOpen ? 'rgba(255, 255, 255, 0.7)' : 'warning.light',
                            letterSpacing: '0.06em'
                        }}
                    >
                        {isOpen ? 'CAJA ABIERTA' : 'CAJA CERRADA'}
                    </Typography>
                    {isOpen && <Counter days={days} hours={hours} minutes={minutes} seconds={seconds} color="white" />}
                </Stack>
            </Stack>
            {isOpen && (
                <Tooltip title="Cerrar caja">
                    <IconButton color="secondary" onClick={() => modalCashRegister.toggle()}>
                        <PowerSettingsNewIcon />
                    </IconButton>
                </Tooltip>
            )}
            <ModalCloseCashRegister modalCloseCashRegister={modalCashRegister} />
        </Stack>
    );
};

export default CashRegisterTime;
